import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Warning,
  Security,
  NetworkCheck,
} from '@mui/icons-material';
import { useWeb3React } from '@web3-react/core';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { isSupportedNetwork, NETWORKS } from '../config/contracts';
import WalletConnection from './WalletConnection';

interface PermissionGuardProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireConnection?: boolean;
}

const PermissionGuard: React.FC<PermissionGuardProps> = React.memo(({
  children,
  requireAdmin = false,
  requireConnection = true,
}) => {
  const { account, chainId, isActive } = useWeb3React();
  const { isAdmin, isLoading: adminLoading, error: adminError } = useAdminAuth();
  const [showWalletConnection, setShowWalletConnection] = React.useState(false);

  // Performance optimization: cache permission check results
  const permissionStatus = useMemo(() => {
    // Quick check: if no connection and no admin rights are required, pass directly
    if (!requireConnection && !requireAdmin) {
      return { canAccess: true, reason: null };
    }

    // Check wallet connection
    if (requireConnection && !isActive) {
      return { canAccess: false, reason: 'wallet_not_connected' };
    }

    // Check network support
    if (isActive && chainId && !isSupportedNetwork(chainId)) {
      return { canAccess: false, reason: 'unsupported_network' };
    }

    // Check admin permissions
    if (requireAdmin) {
      if (adminLoading) {
        return { canAccess: false, reason: 'admin_loading' };
      }
      if (adminError) {
        return { canAccess: false, reason: 'admin_error' };
      }
      if (!isAdmin) {
        return { canAccess: false, reason: 'insufficient_permission' };
      }
    }

    return { canAccess: true, reason: null };
  }, [requireConnection, requireAdmin, isActive, chainId, adminLoading, adminError, isAdmin]);

  // Quick pass check
  if (permissionStatus.canAccess) {
    return <>{children}</>;
  }

  // Render the corresponding interface according to the permission status
  switch (permissionStatus.reason) {
    case 'wallet_not_connected':
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <AccountBalanceWallet
              sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              Wallet Connection Required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please connect your wallet to access this feature
            </Typography>
            {/* <Button
              variant="contained"
              size="large"
              startIcon={<AccountBalanceWallet />}
              onClick={() => setShowWalletConnection(true)}
            >
              Connect Wallet
            </Button> */}
            <WalletConnection
            onConnect={() => setShowWalletConnection(false)}
          />
          </CardContent>
        </Card>
        
        {/* {showWalletConnection && (
          <WalletConnection
            onConnect={() => setShowWalletConnection(false)}
          />
        )} */}
      </Box>
    );

    case 'unsupported_network': {
    const supportedNetworks = Object.values(NETWORKS).map(n => n.name).join(', ');
    
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Card sx={{ maxWidth: 500, textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <NetworkCheck
              sx={{ fontSize: 64, color: 'warning.main', mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              Unsupported Network
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The currently connected network is not supported. Please switch to one of the following networks:
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, fontWeight: 'medium' }}>
              {supportedNetworks}
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              Please manually switch to a supported network in your wallet, or click the button below to try to switch automatically.
            </Alert>
            <Stack direction="column" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
              {/* <Button
                variant="contained"
                onClick={() => setShowWalletConnection(true)}
              >
                Reconnect
              </Button> */}
           <WalletConnection
            onConnect={() => setShowWalletConnection(false)}
            />
            </Stack>
          </CardContent>
        </Card>
        
        {/* {showWalletConnection && (
          <WalletConnection
            onConnect={() => setShowWalletConnection(false)}
          />
        )} */}
      </Box>
    );
    }

    case 'admin_loading':
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
            <CardContent sx={{ p: 4 }}>
              <CircularProgress size={64} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Verifying permissions...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Checking your administrator privileges, please wait
              </Typography>
            </CardContent>
          </Card>
        </Box>
      );

    case 'admin_error':
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
            <CardContent sx={{ p: 4 }}>
              <Warning
                sx={{ fontSize: 64, color: 'error.main', mb: 2 }}
              />
              <Typography variant="h5" gutterBottom>
                Permission verification failed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {adminError}
              </Typography>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </Box>
      );

    case 'insufficient_permission':
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
            <CardContent sx={{ p: 4 }}>
              <Security
                sx={{ fontSize: 64, color: 'error.main', mb: 2 }}
              />
              <Typography variant="h5" gutterBottom>
                Insufficient Permissions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You do not have permission to access this feature. Only contract administrators can access this page.
              </Typography>
              <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                Current account: {account ? `${account.slice(0, 6)}...${account.slice(-4)}` : 'Unknown'}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                If you believe this is an error, please contact the system administrator.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      );

    default:
       return <>{children}</>;
   }
});

export default PermissionGuard;